import { cacheKeys } from "@constants/cache-keys";
import { IS_DEV } from "@constants/env";
import { buildType } from "@helpers/app-identity";
import { useAppVersion } from "@helpers/app-version";
import { hubAxios } from "@helpers/lib/axios";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import { isWeb } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import TooltipWrapper from "@memorang/ui/components/TooltipWrapper";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, IconButton, Text } from "react-native-paper";

const parseVersion = (version: string): number[] => {
	return version.split(".").map((part) => Number.parseInt(part, 10) || 0);
};

const isVersionGreater = (remote?: string, current?: string) => {
	if (!(remote && current)) {
		return false;
	}
	const remoteParts = parseVersion(remote);
	const currentParts = parseVersion(current);

	for (let i = 0; i < Math.max(remoteParts.length, currentParts.length); i++) {
		const remotePart = remoteParts[i] ?? 0;
		const currentPart = currentParts[i] ?? 0;

		if (remotePart > currentPart) {
			return true;
		}
		if (remotePart < currentPart) {
			return false;
		}
	}

	return false;
};

type VersionCheckerContextType = {
	isNewVersionAvailable: boolean;
	currentVersion: string;
	remoteVersion?: string;
	dismissBanner: () => void;
	refreshApp: () => void;
	isDismissed: boolean;
};

const VersionCheckerContext = createContext<VersionCheckerContextType | null>(
	null,
);

const useVersionChecker = () => {
	const context = useContext(VersionCheckerContext);
	if (!context) {
		throw new Error(
			"useVersionChecker must be used within VersionCheckerProvider",
		);
	}
	return context;
};

type VersionCheckerProviderProps = {
	children: ReactNode;
};

const RefreshBanner = () => {
	const { isNewVersionAvailable, dismissBanner, refreshApp } =
		useVersionChecker();
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();

	if (!isNewVersionAvailable) {
		return null;
	}

	const dismissButton = (
		<TooltipWrapper title={t("versionChecker.banner.dismissTooltip")}>
			<IconButton
				icon="close"
				size={18}
				style={{ margin: 0 }}
				iconColor={theme.colors.onSecondaryContainer}
				onPress={dismissBanner}
				accessibilityLabel={t("versionChecker.banner.dismissA11yLabel")}
			/>
		</TooltipWrapper>
	);

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent={isMobile ? "flex-start" : "center"}
			gap={isMobile ? 8 : 20}
			paddingVertical={8}
			paddingLeft={isMobile ? 12 : 16}
			paddingRight={isMobile ? 8 : 16}
			backgroundColor={theme.colors.secondaryContainer}
			// biome-ignore lint/a11y/useSemanticElements: React Native web has no <output> element; status role announces the banner to screen readers
			// @ts-ignore
			role="status"
		>
			<Box
				flexDirection="row"
				alignItems="center"
				gap={8}
				flex={isMobile ? 1 : undefined}
				flexShrink={1}
			>
				<Icon
					source="star-four-points-outline"
					size={18}
					color={theme.colors.onSecondaryContainer}
				/>
				<Text
					variant="bodyMedium"
					style={{
						flexShrink: 1,
						color: theme.colors.onSecondaryContainer,
						fontWeight: "600",
					}}
				>
					{t("versionChecker.banner.message")}
				</Text>
			</Box>
			<Button
				compact
				mode="contained"
				onPress={refreshApp}
				labelStyle={{
					marginVertical: 4,
				}}
				accessibilityLabel={t("versionChecker.banner.refreshA11yLabel")}
			>
				{t("versionChecker.banner.refreshCta")}
			</Button>
			{isMobile ? (
				dismissButton
			) : (
				<Box
					position="absolute"
					right={10}
					top={0}
					bottom={0}
					justifyContent="center"
				>
					{dismissButton}
				</Box>
			)}
		</Box>
	);
};

const VersionCheckerProvider = ({ children }: VersionCheckerProviderProps) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const currentVersion = useAppVersion();

	const { data: remoteVersion } = useQuery({
		queryKey: [cacheKeys.versionChecker, currentVersion, buildType],
		queryFn: async (): Promise<string | undefined> => {
			try {
				const { data } = await hubAxios.get(
					HUB_ENDPOINTS.APP_CONFIG(buildType),
				);
				const version = data?.app?.version;
				return typeof version === "string" ? version : undefined;
			} catch {
				return undefined;
			}
		},
		enabled: isWeb && !!buildType && !IS_DEV,
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchOnMount: true,
		refetchInterval: 60000,
		retry: false,
	});

	const isNewVersionAvailable = !!(
		isWeb &&
		remoteVersion &&
		isVersionGreater(remoteVersion, currentVersion) &&
		!isDismissed &&
		!IS_DEV
	);

	const dismissBanner = () => {
		setIsDismissed(true);
	};

	const refreshApp = () => {
		window.location.reload();
	};

	const contextValue: VersionCheckerContextType = {
		isNewVersionAvailable,
		currentVersion,
		remoteVersion,
		dismissBanner,
		refreshApp,
		isDismissed,
	};

	return (
		<VersionCheckerContext.Provider value={contextValue}>
			{isWeb ? (
				<Box flex={1}>
					<RefreshBanner />
					<Box flex={1}>{children}</Box>
				</Box>
			) : (
				children
			)}
		</VersionCheckerContext.Provider>
	);
};

export default VersionCheckerProvider;
